import React, { Fragment } from 'react';
import { withRouter } from '../../util/withRouter';
import withStyles from '@mui/styles/withStyles';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import gql from 'graphql-tag';
import { compose, Query } from '../../reactApolloCompat';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/en-au';
import './fm_fixes.scss';
import './calendar.css';
import DayToolbar from './DayToolbar';
import ViewEvent from './ViewEvent';
import { EditAppointmentConsumer, withEditAppointment } from '../../context/EditAppointment';
import { getCurrentUser } from '../../environment';
import { joinDefined } from '../../util/strings';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import AppointmentPopover from './AppointmentPopover';
import ViewLocations from './ViewLocations';
import EventConflictPartial from '../../page/fragments/EventConflictPartial';
import { getCalendarClient } from '../../apollo';
import { GQL_REFETCH_60S, subscribe } from '../../util/subscriptions';
import { APPOINTMENT_OPTIONS, getCalendarCategoryByClassName, getCalendarCategoryByType } from './CalendarConstants';
import ErrorFallback from '../../component/ErrorFallback';
import { withErrorBoundary } from '@sentry/react';

class CalendarView extends React.Component {
    constructor(props) {
        super(props);

        this.refetch = null;
        this.startPolling = null;
        this.stopPolling = null;
        this.pollInterval = GQL_REFETCH_60S;

        const saved = JSON.parse(sessionStorage.getItem('calendar')) || {};
        const data = {
            view: saved.view || 'day',
            date: props.match.params.date || saved.date || moment().format('YYYYMMDD'),
            filterStaff: saved.filterStaff || [],
            filterTypes: saved.filterTypes || [],
            filterGroups: saved.filterGroups || ['8'] // default users view, see HP-1531
        };
        const { view, date, filterStaff, filterTypes, filterGroups } = data;
        const range = this.calcViewRange(date, view);
        this.state = {
            date: moment(range.date, 'YYYYMMDD'),
            from: moment(range.from, 'YYYYMMDD').startOf('day'),
            to: moment(range.to, 'YYYYMMDD').startOf('day'),
            offset: 0,
            view,
            filterStaff,
            filterTypes,
            filterGroups,
            groups: [],
            staff: []
        };

        this.staffQueryRef = React.createRef();
        subscribe(this.staffQueryRef, this.pollInterval);
    }

    calcViewRange = (newdate, newview) => {
        let date = newdate;
        date = moment(date).startOf('day');

        let from = date;
        from = moment(from).startOf('day');

        let add = 1;
        let view = newview;
        if (view === 'week') {
            from = moment(from).startOf('week');
            add = 7;
        }
        if (view === 'month') {
            from = moment(from).startOf('month');
            add = moment(from).daysInMonth();
        }
        if (view === 'Locations') {
            from = moment(from).startOf('day');
            add = 7;
        }
        let to = moment(from)
            .add(add, 'days')
            .endOf('day');
        return { date, from, to, view };
    };

    appendCurrentUser = staff => {
        const currentUser = getCurrentUser();
        const me = {
            Name: joinDefined([currentUser.FirstName, (currentUser.Surname || '').slice(0, 1)], ' '),
            ID: '' + currentUser.ID,
            FirstName: currentUser.FirstName,
            Surname: currentUser.Surname,
            resourceId: '' + currentUser.ID
        };
        if (!!staff && staff.length > 0) {
            return [me].concat(staff.filter(e => Number(e.ID) !== Number(me.ID))); // move me to the front!
        }
        return [me];
    };

    setFilters = ({ staff, types, groups }) => {
        const myID = '' + getCurrentUser().ID;
        const excludeMe = staff.filter(e => Number(e) !== Number(myID));
        const newState = {
            ...this.state,
            filterStaff: !!excludeMe && excludeMe.length > 0 ? [myID].concat(excludeMe) : [],
            filterTypes: types,
            filterGroups: groups.map(e => e.ID || e),
            offset: 0
        };
        this.handleRefetch(newState);
    };

    setFromToView = ({ newdate, newview }) => {
        const { view: stateView, date: stateDate } = this.state;
        const { date, from, to, view } = this.calcViewRange(newdate || stateDate, newview || stateView);
        const newState = { ...this.state, date, from, to, view };
        this.handleRefetch(newState);
    };

    handleRefetch = (newState = null) => {
        if (newState) {
            if (this.stopPolling) {
                this.stopPolling();
            }
            this.setState(newState, () => {
                const { view, date, filterStaff, filterTypes, filterGroups } = newState || this.state;
                sessionStorage.setItem(
                    'calendar',
                    JSON.stringify({ date: date.format('YYYYMMDD'), view, filterStaff, filterTypes, filterGroups })
                );
                if (this.startPolling) {
                    setTimeout(() => {
                        this.startPolling(this.pollInterval);
                    }, 10);
                }
            });
        } else {
            this.refetch && this.refetch();
        }
    };

    viewEvent = props => {
        const { date, view } = this.state;
        return (
            <ViewEvent
                {...props}
                view={view}
                date={date}
                category={props.event.ClassName}
                onMutate={() => this.handleRefetch()}
            />
        );
    };
    viewToolbar = props => {
        let { filterStaff, filterTypes, filterGroups, date } = this.state;
        return (
            <DayToolbar
                {...props}
                date={date}
                setFilters={this.setFilters}
                filters={{
                    staff: filterStaff,
                    types: filterTypes,
                    groups: filterGroups
                }}
                getResources={() => this.toolbarResources}
                getGroups={() => this.toolbarGroups}
            />
        );
    };

    timeSlot = props => {
        const timeSlot = moment(props.value); // each timeslot
        timeSlot
            .year(moment().year())
            .month(moment().month())
            .date(moment().date()); // change to today
        const differenceMs = moment() - timeSlot; // compare timeslot to now
        const isCurrentTimeSlot = differenceMs / (60 * 1000) > 0 && differenceMs / (60 * 1000) < 30;
        if (!isCurrentTimeSlot) return props.children; // not current, do nothing.
        if (props.resource) return React.cloneElement(props.children, { 'data-line': true }); // draw the line
        return React.cloneElement(props.children, {
            'data-time': moment().format('h:mm a'), // timestamp
            style: { ...props.children.style, '--offset': Number(moment().format('mm')) - 68 + '%' } // margin
        });
    };

    handleSelectEvent = (event, target) => {
        // let obj = target.currentTarget;
    };

    selectSlot = (slot, resources) => {
        const { createNewAppointment } = this.props;
        if (slot.action !== 'select' || !createNewAppointment) {
            return false;
        }
        const member = resources.filter(e => e.ID === slot.resourceId);
        createNewAppointment(slot.start, slot.end, member);
    };

    renderLoading = (
        <Fragment>
            <div>Loading data, please wait a moment...</div>
            <LinearProgress />
        </Fragment>
    );

    toolbarResources = [];
    toolbarGroups = [];
    setToolbarResources = (resources = []) => (this.toolbarResources = resources);
    setToolbarGroups = (groups = []) => (this.toolbarGroups = groups);
    calComponents = {
        toolbar: this.viewToolbar,
        event: this.viewEvent,
        timeSlotWrapper: props => this.timeSlot(props)
    };

    render() {
        const { onAppointmentPopoverClosed } = this.props;
        const { to, from, view, filterStaff: stateStaff, filterTypes: stateTypes, filterGroups } = this.state;
        const variables = {
            from,
            to,
            memberLimit: 10000,
            filterStaff: [],
            filterTypes: [],
            groupCode: 'fm-witness',
            sortBy: [{ field: 'Sort' }]
        };
        const { ID } = getCurrentUser();

        return (
            <Fragment>
                <Query query={staffQuery} variables={variables}>
                    {staffResults => {
                        const allMemberIDs =
                            staffResults &&
                            staffResults.data &&
                            staffResults.data.readMembers &&
                            staffResults.data.readMembers.edges.map(({ node }) => node.ID);
                        if (!allMemberIDs) return this.renderLoading;
                        const memberIDs = allMemberIDs.concat(ID);
                        const groupStaff = [];
                        staffResults.data.readGroups.forEach(
                            group =>
                                filterGroups.includes(group.ID) &&
                                group.Members.forEach(member => groupStaff.push(member.ID))
                        );
                        const filterStaff =
                            'Locations' !== view
                                ? (stateStaff.length > 0
                                      ? memberIDs.filter(id => !!stateStaff.includes(id))
                                      : [ID]
                                  ).concat(groupStaff)
                                : [];
                        const filterTypes = APPOINTMENT_OPTIONS.map(obj => obj.value)
                            .filter(obj => (stateTypes.length ? stateTypes.includes(obj) : true))
                            .map(obj =>
                                'Locations' === view
                                    ? getCalendarCategoryByType(obj).className.replace(
                                          /^(FuneralManager)([a-z])/i,
                                          '$1\\$2'
                                      )
                                    : obj
                            );
                        const newVariables = { ...variables, filterStaff, filterTypes };
                        const query = 'Locations' !== view ? calendarQuery : locationsQuery;
                        return (
                            <Query
                                client={getCalendarClient()}
                                query={query}
                                variables={newVariables}
                                fetchPolicy="cache-and-network"
                                ref={this.staffQueryRef}
                            >
                                {results => this.renderCalendar(results, staffResults, filterStaff)}
                            </Query>
                        );
                    }}
                </Query>
                <EditAppointmentConsumer>
                    {props => (
                        <AppointmentPopover
                            {...props}
                            allowRecurring
                            onMutate={() => this.handleRefetch()}
                            onClose={() => onAppointmentPopoverClosed()}
                        />
                    )}
                </EditAppointmentConsumer>
            </Fragment>
        );
    }

    renderCalendar = (results, { data: staffData }, includedStaffIDs) => {
        const { loading, error, data, refetch, networkStatus, startPolling, stopPolling } = results;
        const { classes } = this.props;

        if (error) {
            console.error('Failed to load calendar data', error);
            return 'Error loading calendar data';
        }

        if (loading && networkStatus === 1) {
            return this.renderLoading;
        }

        if (refetch) this.refetch = refetch;
        if (startPolling) this.startPolling = startPolling;
        if (stopPolling) this.stopPolling = stopPolling;

        let events = [];
        const { to, filterTypes, view, date } = this.state;
        if (view !== 'Locations') {
            const edges = data && data.readStaffAllocations;
            // const total = edges && data.readCalendarEvents.pageInfo.totalCount;
            events = ((edges && edges.length && edges.filter(e => !!e.ID)) || []).map(e => {
                return {
                    ...e,
                    Start: moment(e.Start).toDate(),
                    End: moment(e.End).toDate(),
                    Title:
                        (!!e.Funeral && e.Funeral.ID !== '0' && e.Funeral.FirstName + ' ' + e.Funeral.Surname) ||
                        e.Title,
                    resourceId: e.Member.ID
                };
            });
        } else {
            const edges = data && data.readCalendarEvents;
            events = ((edges && edges.length && edges.filter(e => !!e.ID)) || []).map(e => ({
                ...e,
                Start: moment(e.Start).toDate(),
                End: moment(e.End).toDate(),
                Title: (!!e.Funeral && e.Funeral.ID !== '0' && e.Funeral.FirstName + ' ' + e.Funeral.Surname) || e.Title
            }));
        }

        const memberEdges = staffData && staffData.readMembers && staffData.readMembers.edges;
        let resources =
            (memberEdges &&
                memberEdges.length &&
                memberEdges.map(e => {
                    return {
                        ...e.node,
                        Name: e.node.FirstName + ' ' + (e.node.Surname ? e.node.Surname : ''),
                        resourceId: e.node.ID
                    };
                })) ||
            [];

        const groups = (staffData && staffData.readGroups) || [];

        let filteredStaff = [];
        if (includedStaffIDs.length > 0) {
            filteredStaff = filteredStaff.concat(resources.filter(e => includedStaffIDs.includes(e.resourceId)));
        } else {
            filteredStaff = resources.slice(0);
        }
        filteredStaff = [...new Set(filteredStaff)]; // omit dupes

        const dayPropGetter = slotDate => {
            const classes = [];
            if (date && moment(slotDate).isSame(date, 'day')) {
                classes.push('rbc-selected');
            }
            if (moment(slotDate).day() === 0 || moment(slotDate).day() === 6) {
                classes.push('rbc-weekend');
            }
            return classes.length ? { className: classes.join(' ') } : {};
        };

        const showMore = (total, extraEs) => {
            const types = [];
            extraEs.forEach(obj => types.push(obj.Type));
            const myTypes = [...new Set(types)];
            return (
                <>
                    + {extraEs.length} more&nbsp;
                    {myTypes.map(obj => (
                        <span key={obj} style={{ color: getCalendarCategoryByType(obj).color }}>
                            &#x25FC;
                        </span>
                    ))}
                </>
            );
        };

        resources = this.appendCurrentUser(resources);

        this.setToolbarResources(resources);
        this.setToolbarGroups(groups.filter(g => g.Code !== 'fm-witness' && g.Code !== 'cm-witness')); // omit root group

        if (filterTypes.length) {
            if ('Locations' === view) {
                events = events.filter(obj => {
                    const { type } = getCalendarCategoryByClassName(obj.ClassName, obj.CrematedOrBuried);
                    return filterTypes.includes(type);
                });
            }
        }

        const minSlot = moment()
            .startOf('day')
            .add(6, 'hours') // start at 6am
            .toDate();
        const maxSlot = moment()
            .endOf('day')
            .subtract(1, 'hours') // end before 11pm
            .toDate();

        const localiser = momentLocalizer(moment);
        localiser.startAndEndAreDateOnly = () => false; // prevent allDay rows
        return (
            <Fragment>
                <Paper className={classes.paper} elevation={0}>
                    <Calendar
                        className={'rbc-view-' + view}
                        dayPropGetter={dayPropGetter}
                        loading={networkStatus}
                        popup
                        selectable //is broken!
                        onSelectSlot={slotInfo => this.selectSlot(slotInfo, resources)}
                        components={this.calComponents}
                        events={events || []}
                        locationEvents={events || []}
                        defaultView={view}
                        views={{ day: true, Locations: ViewLocations }}
                        onSelectEvent={this.handleSelectEvent}
                        date={moment(date).toDate()}
                        scrollToTime={moment()
                            .subtract(30, 'minutes')
                            .startOf('hour')}
                        onNavigate={newdate => this.setFromToView({ newdate })}
                        onView={newview => this.setFromToView({ newview })}
                        showMultiDayTimes // prevent allDay rows
                        startAccessor="Start"
                        endAccessor="End"
                        resources={this.appendCurrentUser(filteredStaff)}
                        resourceIdAccessor="resourceId" //I cannot get 'memberID' to work :(
                        resourceTitleAccessor="Name"
                        min={minSlot}
                        max={maxSlot}
                        messages={{ showMore }}
                        formats={{ timeGutterFormat: 'hA' }}
                        localizer={localiser}
                        length={7} // date range for locations view
                        to={moment(to).toDate()} // for Locations
                    />
                    {(() => {
                        if (loading) {
                            return <LinearProgress />;
                        }
                    })()}
                </Paper>
            </Fragment>
        );
    };
}

const locationsQuery = gql`
    query ReadCalendarEvents($from: String!, $to: String!, $filterStaff: [ID], $filterTypes: [String]) {
        readCalendarEvents(from: $from, to: $to, filterStaff: $filterStaff, filterTypes: $filterTypes) {
            ID
            ClassName
            Title
            Start
            End
            Notes
            Location {
                ID
                Name
                Suburb
            }
            LocationFlattened
            StaffAllocations {
                ID
                Allocation
                Member {
                    ID
                    FirstName
                    Surname
                }
                Start
                End
            }
            EventConflicts {
                ${EventConflictPartial}
            }
            ... on CalendarEvent {
                ID
            }
            ... on FuneralDisposal {
                CrematedOrBuried
                CemeterySection #Comment
                Funeral {
                    ID
                    LegacyKey
                    FirstName
                    Surname
                }
            }
            ... on RefreshmentsVenue {
                Comment
                Funeral {
                    ID
                    LegacyKey
                    FirstName
                    Surname
                }
            }
            ... on FuneralPlaceOfService {
                Comment
                Funeral {
                    ID
                    LegacyKey
                    FirstName
                    Surname
                }
            }
            ... on FuneralPlaceOfViewing {
                Comment
                Funeral {
                    ID
                    LegacyKey
                    FirstName
                    Surname
                }
            }
            ... on ReflectionRoom {
                Comment
                Funeral {
                    ID
                    LegacyKey
                    FirstName
                    Surname
                }
            }
        }
    }
`;

const calendarQuery = gql`
    query ReadStaffAllocations($from: String!, $to: String!, $filterStaff: [ID], $filterTypes: [String]) {
        readStaffAllocations(from: $from, to: $to, filterStaff: $filterStaff, filterTypes: $filterTypes) {
            ID
            Title
            Start
            End
            Type
            Allocation
            LocationFlattened
            Recurring
            Appointment {
                ID
                Reason
                Office
                Comment
                Phone
                LocationType
                Members {
                    ID
                    FirstName
                    Surname
                }
                Enquiry {
                    ID
                    LegacyKey
                    EnquirerGivenName
                    EnquirerSurname
                    NameOfDeceased
                    Funeral {
                        ID
                        LegacyKey
                        FirstName
                        Surname
                    }
                }
            }
            Member {
                ID
                FirstName
                Surname
            }
            Funeral {
                ID
                LegacyKey
                FirstName
                Surname
            }
            Service {
                ID
                Type
                Start
                StaffAllocations {
                    ID
                    Allocation
                    Member {
                        ID
                        FirstName
                        Surname
                    }
                    Start
                    End
                }
            }
            Viewing {
                ID
                Type
                Start
                StaffAllocations {
                    ID
                    Allocation
                    Member {
                        ID
                        FirstName
                        Surname
                    }
                    Start
                    End
                }
            }
            Disposal {
                ID
                Start
                StaffAllocations {
                    ID
                    Allocation
                    Member {
                        ID
                        FirstName
                        Surname
                    }
                    Start
                    End
                }
            }
            Refreshments {
                ID
                Start
                StaffAllocations {
                    ID
                    Allocation
                    Member {
                        ID
                        FirstName
                        Surname
                    }
                    Start
                    End
                }
            }
            ReflectionRoom {
                ID
                ClassName
                Title
                Start
                End
                LocationFlattened
                Location {
                    ID
                    Name
                    Suburb
                }
                StaffAllocations {
                    ID
                    Allocation
                    Member {
                        ID
                        FirstName
                        Surname
                    }
                    Start
                    End
                }
                Funeral {
                    ID
                    LegacyKey
                    FirstName
                    Surname
                }
            }
        }
    }
`;

const staffQuery = gql`
    query ReadStaffAndGroups($groupCode: String, $memberLimit: Int, $sortBy: [SortField]) {
        readMembers(groupCode: $groupCode, limit: $memberLimit, sortBy: $sortBy) {
            edges {
                node {
                    ID
                    FirstName
                    Surname
                    Email
                }
            }
        }
        readGroups(parentGroupCode: "fm-user") {
            ID
            Title
            Code
            Members {
                ID
            }
        }
    }
`;

const styles = ({ spacing, typography, breakpoints }) => ({
    pageTitle: {
        fontWeight: typography.fontWeightLight
    },
    content: {
        padding: spacing(4)
    },
    paper: {
        padding: 0,
        height: '100%',
        backgroundColor: 'transparent',
        border: 'none!important',
        [breakpoints.down('sm')]: {
            padding: 0
        }
    },
    rbcHeader: {
        minWidth: '100px'
    }
});

export default withErrorBoundary(
    compose(withRouter, withEditAppointment, withSnackbarMessage, withStyles(styles))(CalendarView),
    { fallback: ErrorFallback }
);
